import React, { useMemo, useState } from "react";
import Lottie from "lottie-react";
import { useStreakData } from "../../hooks/useStreakData";
import {
  getEarnedBadges,
  getNextBadge,
  getBadgeProgress,
  getLastMilestone,
} from "../../constants/streakBadges";
import { StreakStatus } from "../../services/streakService";
import StreakMilestonesDialog from "./StreakMilestonesDialog";

// ---------------------------------------------------------------------------
// Day-of-week label (index 0 = 6 days ago, 6 = today)
// ---------------------------------------------------------------------------
const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
function dayLabel(dateID: string): string {
  const y = parseInt(dateID.slice(0, 4), 10);
  const m = parseInt(dateID.slice(4, 6), 10) - 1;
  const d = parseInt(dateID.slice(6, 8), 10);
  return DAY_LABELS[new Date(y, m, d).getDay()];
}

// ---------------------------------------------------------------------------
// State config
// ---------------------------------------------------------------------------
interface StateConfig {
  label: string; // small label above the big number
  headline: (streak: number, daysLeft: number, prevStreak: number) => string;
  subline: (streak: number, daysLeft: number, prevStreak: number) => string;
  accentClass: string;
  bgClass: string;
  barClass: string;
}

const STATE_CONFIG: Record<StreakStatus, StateConfig> = {
  new: {
    label: "Log streak",
    headline: () => "Start today",
    subline: () => "Log anything to begin your streak",
    accentClass: "text-base-content",
    bgClass: "bg-base-100",
    barClass: "bg-base-300",
  },
  active: {
    label: "Current streak",
    headline: (streak) => `${streak} day${streak === 1 ? "" : "s"}`,
    subline: () => "Keep going — you're on a roll!",
    accentClass: "text-base-content",
    bgClass: "bg-base-100",
    barClass: "bg-primary",
  },
  warning: {
    label: "Streak at risk",
    headline: (_s, daysLeft) =>
      `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`,
    subline: (streak) => `Log today to protect your ${streak}-day streak`,
    accentClass: "text-amber-500",
    bgClass: "bg-amber-50",
    barClass: "bg-amber-400",
  },
  lost: {
    label: "Streak ended",
    headline: () => "Start fresh",
    subline: (_s, _d, prev) =>
      prev > 0
        ? `Your ${prev}-day streak ended. Today is day 1.`
        : "Log anything to begin a new streak",
    accentClass: "text-base-content/50",
    bgClass: "bg-base-100",
    barClass: "bg-base-300",
  },
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface StreakCardProps {
  userId: string;
  longestStreak?: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const StreakCard: React.FC<StreakCardProps> = ({ userId, longestStreak }) => {
  const { streakResult, loading } = useStreakData(userId);
  const [showMilestones, setShowMilestones] = useState(false);

  const currentStreak = streakResult?.currentStreak ?? 0;
  const status = streakResult?.status ?? "new";
  const last7Days = streakResult?.last7Days ?? [];
  const daysSince = streakResult?.daysSinceLastLog ?? 10;
  const prevStreak = streakResult?.previousStreakBeforeLost ?? 0;
  const daysLeft = Math.max(0, 7 - daysSince);

  const earnedBadges = useMemo(
    () => getEarnedBadges(currentStreak),
    [currentStreak],
  );
  const nextBadge = useMemo(() => getNextBadge(currentStreak), [currentStreak]);
  const progress = useMemo(
    () => getBadgeProgress(currentStreak),
    [currentStreak],
  );
  const lastMilestone = useMemo(
    () => getLastMilestone(currentStreak),
    [currentStreak],
  );

  const cfg = STATE_CONFIG[status];

  // ── Peak score → color (matches dashboard peak score color guide) ────────
  const getPeakScoreColor = (score: number): string => {
    if (score > 100) return "#D4AF37"; // Gold
    if (score >= 80) return "#0096c7"; // Blue (PEAK primary)
    if (score >= 60) return "#22c55e"; // Green
    if (score >= 30) return "#f97316"; // Orange
    return "#ef4444"; // Red
  };

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading && !streakResult) {
    return (
      <div className="bg-base-100 rounded-none md:rounded-lg px-8 py-6 mt-3 animate-pulse">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-base-300 rounded-full" />
          <div className="space-y-1.5 flex-1">
            <div className="h-3 bg-base-300 rounded w-20" />
            <div className="h-6 bg-base-300 rounded w-28" />
            <div className="h-3 bg-base-300 rounded w-44" />
          </div>
        </div>
        <div className="h-1.5 bg-base-300 rounded-full mb-4" />
        <div className="flex gap-2">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="flex-1 h-8 bg-base-300 rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={`${cfg.bgClass} rounded-none md:rounded-lg px-8 py-6 mt-3 cursor-pointer active:scale-[0.99]`}
        onClick={() => setShowMilestones(true)}
      >
        {/* ── Main content: left stats + right 7-day dots ──────────────────── */}
        <div className="flex items-center justify-between">
          {/* Left side — title + big stat + subline */}
          <div className="flex-1 min-w-0">
            <h3 className="text-[14px] font-heading mb-2">
              Log{" "}
              <span className="font-grotesk text-base-content/60">Streak</span>
            </h3>
            <div className="flex items-center space-x-2 mb-1">
              <span
                className={`text-[48px] font-bold font-heading leading-none ${cfg.accentClass}`}
              >
                {currentStreak}
              </span>
              <span className="text-[28px] font-light text-base-content/50 leading-none">
                day{currentStreak === 1 ? "" : "s"}
              </span>

              {/* Earned badge — show latest */}
              {earnedBadges.length > 0 && (
                <div title={`${earnedBadges[earnedBadges.length - 1].name}`}>
                  <Lottie
                    animationData={earnedBadges[earnedBadges.length - 1].lottie}
                    loop={false}
                    autoplay
                    style={{ width: 28, height: 28 }}
                  />
                </div>
              )}
            </div>
            <p className="text-[12px] text-base-content/60">
              {cfg.subline(currentStreak, daysLeft, prevStreak)}
            </p>
          </div>

          {/* Right side — 7-day peak score bars */}
          {last7Days.length === 7 && (
            <div className="flex flex-col items-center mt-2">
              <div className="flex space-x-1 items-end">
                {last7Days.map((day, i) => {
                  const hasPeak = day.peakScore != null && day.peakScore > 0;
                  const score = Math.round(day.peakScore ?? 0);
                  const MAX_PEAK = 110;
                  const fillPercent = hasPeak
                    ? Math.min(score / MAX_PEAK, 1) * 100
                    : 0;
                  return (
                    <div
                      key={i}
                      className="w-4 h-12 relative overflow-hidden rounded-full"
                      title={`${day.dateID} — ${hasPeak ? `Peak: ${score}` : "No peak score"}`}
                    >
                      <div className="absolute inset-0 bg-base-300" />
                      {hasPeak && (
                        <div
                          className="absolute bottom-0 left-0 right-0 transition-all duration-500 rounded-full"
                          style={{
                            height: `${fillPercent}%`,
                            backgroundColor: getPeakScoreColor(score),
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Day labels */}
              <div className="flex space-x-1 mt-1">
                {last7Days.map((day, i) => {
                  const isToday = i === 6;
                  return (
                    <div
                      key={`${day.dateID}-label`}
                      className="w-4 text-center"
                    >
                      <span
                        className={`text-xs font-medium ${
                          isToday
                            ? "text-primary font-semibold"
                            : "text-base-content/60"
                        }`}
                      >
                        {isToday ? "T" : dayLabel(day.dateID)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Progress toward next badge ────────────────────────────────────── */}
        {nextBadge && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <div title={`${nextBadge.name} — ${nextBadge.description}`}>
                  <Lottie
                    animationData={nextBadge.lottie}
                    loop={false}
                    autoplay
                    style={{ width: 18, height: 18 }}
                  />
                </div>
                <span className="text-[11px] font-medium text-base-content/60">
                  {nextBadge.name}
                </span>
                <span className="text-[11px] text-base-content/40">
                  · {nextBadge.days} days
                </span>
              </div>
              <span className="text-[11px] font-medium text-base-content/60">
                {nextBadge.days - currentStreak} to go
              </span>
            </div>

            {/* Progress bar */}
            <div className="h-1.5 bg-base-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${cfg.barClass}`}
                style={{ width: `${Math.max(progress * 100, 2)}%` }}
              />
            </div>

            {lastMilestone > 0 && (
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-base-content/30">
                  {lastMilestone}d
                </span>
                <span className="text-[10px] text-base-content/30">
                  {nextBadge.days}d
                </span>
              </div>
            )}
          </div>
        )}

        {/* ── Longest streak footer ─────────────────────────────────────────── */}
        {longestStreak !== undefined && longestStreak > 0 && (
          <div className="mt-3 pt-2 border-t border-base-200 flex items-center gap-1.5">
            <span className="text-[11px] text-base-content/40">
              Best streak
            </span>
            <span className="text-[11px] font-semibold text-base-content/60">
              {longestStreak} {longestStreak === 1 ? "day" : "days"}
            </span>
          </div>
        )}
      </div>

      {/* Milestones dialog */}
      <StreakMilestonesDialog
        isOpen={showMilestones}
        onClose={() => setShowMilestones(false)}
        currentStreak={currentStreak}
      />
    </>
  );
};

export default StreakCard;
